import React from "react";
import { Monitor, Lock, Zap, ShieldCheck, CheckCircle2, Download, ExternalLink, Phone, CalendarDays } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CTABanner from "../components/CTABanner";
import ITSupportTeaser from "../components/ITSupportTeaser";
import { images, siteInfo, tariefs } from "../mock";
import { useBooking } from "../context/BookingContext";

const steps = [
  {
    icon: Phone,
    title: "Neem contact op",
    description:
      "Bel ons op 06-12562049 of plan een online afspraak in. Wij beoordelen of hulp op afstand mogelijk is voor uw probleem.",
  },
  {
    icon: Download,
    title: "Download Splashtop SOS",
    description:
      "Wij gebruiken Splashtop SOS — een veilige en bewezen remote support tool. U krijgt van ons een directe downloadlink (gratis, geen installatie nodig).",
  },
  {
    icon: Lock,
    title: "Deel uw 9-cijferige code",
    description:
      "Na het openen van Splashtop krijgt u een unieke code. Geef deze door aan onze technicus — alleen dan kunnen wij verbinding maken.",
  },
  {
    icon: Monitor,
    title: "Wij kijken & lossen op",
    description:
      "Onze technicus maakt een veilige verbinding en lost het probleem op terwijl u meekijkt. Na afloop wordt de verbinding automatisch verbroken.",
  },
];

const HulpOpAfstand = () => {
  const booking = useBooking();
  return (
    <>
      <PageHeader
        eyebrow="— Hulp op afstand"
        title="Direct geholpen, zonder de deur uit"
        subtitle="Veel problemen zijn snel op te lossen via een beveiligde externe verbinding met Splashtop. Efficient, voordelig en zonder wachttijd."
      />

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-5 leading-tight">
                Wat is hulp op afstand?
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-5">
                Met een veilige externe verbinding via Splashtop kan onze technicus direct
                meekijken op uw computer. Zo lossen wij veel problemen binnen enkele minuten op
                — zonder dat u de deur uit hoeft.
              </p>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Denk aan software installatie, instellingen aanpassen, trage computer, of
                e-mail problemen. Alles 100% veilig én u kijkt live mee.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 text-sm">
                <div className="border border-neutral-200 p-4">
                  <p className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Particulier</p>
                  <p className="font-bold text-neutral-900 text-lg">{tariefs.remoteParticulier.hourly} <span className="font-normal text-neutral-500 text-sm">/ uur incl. BTW</span></p>
                  <p className="text-xs text-neutral-600 mt-1">of <strong>{tariefs.remoteParticulier.per15}</strong> per 15 minuten</p>
                </div>
                <div className="border border-neutral-200 p-4">
                  <p className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Zakelijk</p>
                  <p className="font-bold text-neutral-900 text-lg">{tariefs.remoteZakelijk.hourly} <span className="font-normal text-neutral-500 text-sm">/ uur excl. BTW</span></p>
                  <p className="text-xs text-neutral-600 mt-1">of <strong>{tariefs.remoteZakelijk.per15}</strong> per 15 minuten</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { icon: Zap, label: "Direct beschikbaar" },
                  { icon: ShieldCheck, label: "100% veilig" },
                  { icon: Monitor, label: "U kijkt mee" },
                  { icon: Lock, label: "Versleutelde verbinding" },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-center gap-3 p-4 bg-neutral-50 border border-neutral-200">
                      <Icon size={18} className="text-neutral-900" />
                      <span className="text-sm font-medium text-neutral-900">{item.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={booking.open}
                  className="inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-black text-white px-6 py-3 text-sm font-medium transition-colors"
                >
                  <CalendarDays size={15} />
                  Plan hulp op afstand in
                </button>
                <a
                  href={`tel:${siteInfo.phoneRaw}`}
                  className="inline-flex items-center justify-center gap-2 border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-6 py-3 text-sm font-medium transition-colors"
                >
                  <Phone size={15} />
                  Bel direct
                </a>
              </div>
            </div>
            <img
              src={images.professional}
              alt="Hulp op afstand"
              loading="lazy"
              className="w-full h-[520px] object-cover rounded"
            />
          </div>
        </div>
      </section>

      {/* Splashtop info card */}
      <section className="pb-4 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10">
          <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 text-white p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-14 h-14 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={24} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <p className="text-xs uppercase tracking-[0.25em] text-neutral-400">Remote support tool</p>
              </div>
              <h3 className="text-2xl font-bold mb-2">Wij werken met Splashtop SOS</h3>
              <p className="text-neutral-300 leading-relaxed max-w-2xl">
                Splashtop is een veilige, wereldwijd gebruikte remote support oplossing. Geen
                account nodig, geen installatie — gewoon downloaden, code doorgeven, en wij helpen.
                U ontvangt de downloadlink zodra uw afspraak bevestigd is.
              </p>
            </div>
            <a
              href="https://sos.splashtop.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-white text-neutral-900 hover:bg-neutral-100 px-6 py-3 text-sm font-medium transition-colors flex-shrink-0"
            >
              <Download size={15} />
              Splashtop SOS
              <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-sm tracking-[0.3em] uppercase text-neutral-500 mb-3">
              — In 4 stappen
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900">
              Hoe werkt hulp op afstand?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-neutral-200">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="p-8 bg-white border-r border-b border-neutral-200 relative">
                  <span className="absolute top-3 right-4 text-5xl font-bold text-neutral-100">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="w-12 h-12 bg-neutral-900 text-white flex items-center justify-center mb-5">
                    <Icon size={20} />
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{s.description}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-8 p-6 bg-amber-50 border border-amber-200 flex items-start gap-3">
            <ShieldCheck size={22} className="text-amber-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              <strong>Uw veiligheid eerst:</strong> Wij hebben alleen toegang tot uw computer als
              ú ons de 9-cijferige Splashtop-code geeft. Na afloop wordt de verbinding volledig
              verbroken en heeft niemand meer toegang.
            </p>
          </div>
        </div>
      </section>

      <ITSupportTeaser variant="afstand" />

      <CTABanner />
    </>
  );
};

export default HulpOpAfstand;
